import { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { useQueryClient } from '@tanstack/react-query';

import { comoErroDaApi } from '../../shared/api/erros';
import { chavesDeFaturamento, importarConferencia } from './api/faturamento';

/**
 * Espelha `FaturamentoEndpoints.TamanhoMaximoDoXml` (`apps/api`, `50 * 1024 *
 * 1024`). Mesma disciplina de `EnvioDeFonte.LIMITE_DE_TAMANHO_BYTES`
 * (`features/contratos`): pré-cheque do front, a API confere de novo.
 */
export const LIMITE_DE_TAMANHO_BYTES = 50 * 1024 * 1024;

/** Pré-cheque do front: extensão, tamanho e um indício mínimo de XML bem-formado. `null` = passou. */
async function validarArquivo(arquivo: File): Promise<string | null> {
  if (arquivo.size > LIMITE_DE_TAMANHO_BYTES) {
    return `Arquivo grande demais: o limite é ${String(Math.floor(LIMITE_DE_TAMANHO_BYTES / (1024 * 1024)))} MB.`;
  }
  if (!arquivo.name.toLowerCase().endsWith('.xml')) {
    return 'Esse arquivo não é um XML (a extensão não é .xml).';
  }
  const inicio = await arquivo.slice(0, 256).text();
  if (!inicio.trimStart().startsWith('<')) {
    return 'Esse arquivo não parece um XML (o conteúdo não começa com "<").';
  }
  return null;
}

/**
 * Importação do XML TISS do lote (`TP-052`, T8/T9). Nenhum botão de envio
 * aqui nem em nenhuma outra tela desta feature: o que entra é só o que o
 * IW já fechou, importado por uma pessoa — o envio ao IW ou à operadora é
 * o `E9-07`, fora de escopo desta fase (restrição 9).
 */
export function ImportarConferencia({ contratoId }: { contratoId: string }): React.JSX.Element {
  const cliente = useQueryClient();

  const [arquivo, definirArquivo] = useState<File | null>(null);
  const [nomeDoArquivoRecusado, definirNomeDoArquivoRecusado] = useState<string | null>(null);
  const [motivoDaRecusaLocal, definirMotivoDaRecusaLocal] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);
  const [erroDoEnvio, definirErroDoEnvio] = useState<string | null>(null);
  const [resultado, definirResultado] = useState<{ conferenciaId: string; criada: boolean } | null>(null);
  const entradaDeArquivo = useRef<HTMLInputElement>(null);

  const selecionar = (lista: FileList | null): void => {
    const escolhido = lista?.[0];
    if (escolhido === undefined) return;
    definirResultado(null);
    definirErroDoEnvio(null);
    void validarArquivo(escolhido).then((motivo) => {
      if (motivo !== null) {
        definirArquivo(null);
        definirNomeDoArquivoRecusado(escolhido.name);
        definirMotivoDaRecusaLocal(motivo);
        return;
      }
      definirArquivo(escolhido);
      definirNomeDoArquivoRecusado(null);
      definirMotivoDaRecusaLocal(null);
    });
  };

  const enviar = (): void => {
    if (arquivo === null || contratoId === '') return;
    definirEnviando(true);
    definirErroDoEnvio(null);
    definirResultado(null);
    importarConferencia({ contratoId, arquivo })
      .then(({ conferencia, criada }) => {
        definirResultado({ conferenciaId: conferencia.conferenciaId, criada });
        void cliente.invalidateQueries({ queryKey: chavesDeFaturamento.raiz });
      })
      .catch((causa: unknown) => {
        definirErroDoEnvio(comoErroDaApi(causa).message);
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  const podeEnviar = arquivo !== null && contratoId !== '' && !enviando;

  return (
    <Stack spacing={1.5} component="form" aria-label="Importar lote (XML TISS)" onSubmit={(evento) => { evento.preventDefault(); enviar(); }}>
      <Typography variant="h3">Importar lote</Typography>
      <Typography variant="body2" color="text.secondary">
        O XML TISS 4.01 que o IW já fechou. Esta tela nunca envia nada ao IW nem à operadora — ela
        só lê o que foi importado e confere contra as regras vigentes.
      </Typography>

      {contratoId === '' ? (
        <Alert severity="info" role="status">
          Escolha um contrato acima para importar um lote.
        </Alert>
      ) : null}

      <Box
        role="button"
        tabIndex={0}
        aria-label="Área para escolher o XML do lote"
        onClick={() => entradaDeArquivo.current?.click()}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' || evento.key === ' ') entradaDeArquivo.current?.click();
        }}
        sx={(tema) => ({
          border: `2px dashed ${tema.palette.divider}`,
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
        })}
      >
        <input
          ref={entradaDeArquivo}
          type="file"
          accept="application/xml,.xml"
          hidden
          aria-label="Escolher o XML do lote"
          onChange={(evento) => {
            selecionar(evento.target.files);
          }}
        />
        <UploadFileOutlined sx={(tema) => ({ fontSize: 32, color: tema.palette.text.secondary })} />
        <Typography variant="body2">Clique para escolher o XML do lote.</Typography>
        {arquivo !== null ? (
          <Typography variant="caption" color="text.secondary">
            {arquivo.name}
          </Typography>
        ) : null}
      </Box>

      {motivoDaRecusaLocal !== null ? (
        <Alert severity="warning" role="alert">
          {nomeDoArquivoRecusado !== null ? `"${nomeDoArquivoRecusado}": ` : ''}
          {motivoDaRecusaLocal} Nada foi enviado.
        </Alert>
      ) : null}

      <Box>
        <Button type="submit" variant="contained" disabled={!podeEnviar}>
          Importar
        </Button>
      </Box>

      {erroDoEnvio !== null ? <Alert severity="error" role="alert">{erroDoEnvio}</Alert> : null}

      {resultado !== null ? (
        <Alert severity={resultado.criada ? 'success' : 'info'} role="status">
          {resultado.criada
            ? 'Lote importado e conferido contra as regras vigentes.'
            : 'Esse arquivo já tinha sido importado antes. Esta é a conferência existente.'}
        </Alert>
      ) : null}
    </Stack>
  );
}
